// The meetings page's dialogs (Record, Import, Resume recording) in the meetings look
// (meetings.css: .ws-modal-scrim, .ws-meeting-dialog) on Radix Dialog, so Esc closes them,
// focus stays inside while they are open and returns to the button that opened them.
//
// A press on the scrim closes a dialog only when nothing typed would be lost: Record and
// Import hold a form, so they pass `keepOnOutsidePress`. `closable={false}` holds a dialog
// open while it works (an upload in flight).
//
// Not a second dialog primitive: DESIGN-SPEC §6.10 moves these dialogs onto the restyled kit
// Dialog (components/ui/dialog.tsx). Its content brings its own overlay, centred geometry and
// × button, so that move is a visual change for its own PR; this wrapper only gives today's
// look the kit's keyboard and focus behaviour until then.
import { useRef, type ReactNode } from 'react';
import { Dialog } from 'radix-ui';

export function MeetingDialog({
  label,
  className = '',
  portal = false,
  keepOnOutsidePress = false,
  closable = true,
  onClose,
  children,
}: {
  /** The dialog's name; the visible heading is `MeetingDialogTitle`. */
  label: string;
  className?: string;
  /** Mount on <body> instead of in place, as Resume (opened from the recording strip) does. */
  portal?: boolean;
  keepOnOutsidePress?: boolean;
  closable?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  // There is no Dialog.Trigger (each caller opens it from its own button), so focus goes back
  // to whatever held it when the dialog opened, as meeting-reminders.tsx does.
  const opener = useRef<HTMLElement | null>(null);
  const scrim = (
    <Dialog.Overlay className="ws-modal-scrim">
      <Dialog.Content
        className={`ws-meeting-dialog ${className}`.trim()}
        aria-label={label}
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          opener.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (opener.current?.isConnected) opener.current.focus();
          opener.current = null;
        }}
        onEscapeKeyDown={(event) => {
          if (!closable) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (keepOnOutsidePress || !closable) event.preventDefault();
        }}
      >
        {children}
      </Dialog.Content>
    </Dialog.Overlay>
  );
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {portal ? <Dialog.Portal>{scrim}</Dialog.Portal> : scrim}
    </Dialog.Root>
  );
}

/** The dialog's visible heading, which also names it for assistive technology. */
export function MeetingDialogTitle({ children }: { children: ReactNode }) {
  return (
    <Dialog.Title asChild>
      <h2>{children}</h2>
    </Dialog.Title>
  );
}
